import { useMemo, useState } from 'react';
import {
  useCan,
  useCustom,
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import {
  Typography,
  Tag,
  Table,
  Space,
  Select,
  Button,
  Modal,
  Alert,
} from 'antd';

const { Title } = Typography;

export type IssuedLicenceRow = {
  id: number;
  account_id: number;
  account_email: string | null;
  publication_id: number;
  product_kind: string;
  lcp_license_id: string;
  revoked_at: string | null;
  revoke_reason_code: string | null;
  revoked_by_user_id: number | null;
  lsd_terminal_status: string | null;
  registered_device_count_max_observed: number | null;
  overshare_flagged_at: string | null;
  overshare_note: string | null;
  last_lsd_status_checked_at: string | null;
};

const OVERSHARE_DEVICE_THRESHOLD = 6;

type IssuedLicenceRevokeSingleOut = {
  success: boolean;
  lcp_license_id: string;
  upstream_status: number;
  detail?: string | null;
};

type IssuedLicenceRevokeAllOut = {
  success_ids: string[];
  failed: Array<{ lcp_license_id: string; status_code: number }>;
};

export type IssuedLicencesPanelProps = {
  /** Distinct account ids (e.g. from user profiles or manual selection). */
  accountIds: number[];
  /** When true and no account selected, query all issued licences. */
  allowAllWhenNoAccount?: boolean;
  accountEmailQuery?: string;
  /** After revoke, invalidate ``iam.users`` detail for this id (optional). */
  invalidateIamUserId?: number | null;
};

/** Issued LCPS licences for one selected account: table + revoke actions (gated by ``account.issued_licences``). */
export function IssuedLicencesPanel({
  accountIds,
  allowAllWhenNoAccount = false,
  accountEmailQuery,
  invalidateIamUserId,
}: IssuedLicencesPanelProps) {
  const t = useTranslate();
  const { open } = useNotification();
  const invalidate = useInvalidate();
  const { mutate: mutateRevoke, mutation: revokeMutation } =
    useCustomMutation();

  const { data: canRevoke } = useCan({
    resource: 'account.issued_licences',
    action: 'revoke',
  });

  const sortedIds = useMemo(
    () => [...accountIds].sort((a, b) => a - b),
    [accountIds]
  );
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
    null
  );
  const accountId = useMemo(() => {
    if (sortedIds.length === 0) {
      return null;
    }
    if (selectedAccountId != null && sortedIds.includes(selectedAccountId)) {
      return selectedAccountId;
    }
    return sortedIds[0] ?? null;
  }, [sortedIds, selectedAccountId]);
  const accountEmailParam = accountEmailQuery?.trim().replace(/\//g, '.');
  const listUrl =
    accountId != null
      ? `account/issued-licences/accounts/${accountId}`
      : allowAllWhenNoAccount
        ? `account/issued-licences${
            accountEmailParam
              ? `?account_email=${encodeURIComponent(accountEmailParam)}`
              : ''
          }`
        : '';

  const { query: issuedLicencesQuery } = useCustom<IssuedLicenceRow[]>({
    url: listUrl,
    method: 'get',
    queryOptions: {
      enabled: listUrl.length > 0,
    },
  });

  const maybeInvalidateUser = () => {
    if (invalidateIamUserId != null) {
      invalidate({
        resource: 'iam.users',
        invalidates: ['detail', 'list'],
        id: invalidateIamUserId,
      });
    }
  };

  const handleRevokeSingle = (row: IssuedLicenceRow) => {
    const effectiveAccountId = accountId ?? row.account_id;
    if (!canRevoke?.can || effectiveAccountId == null) {
      return;
    }
    Modal.confirm({
      title: t('iam.users.licences.revoke_confirm_title'),
      content: t('iam.users.licences.revoke_confirm_description'),
      onOk: () => {
        mutateRevoke(
          {
            url: `account/issued-licences/accounts/${effectiveAccountId}/${row.lcp_license_id}/revoke`,
            method: 'post',
            values: {},
          },
          {
            onSuccess: async (result) => {
              const body = result?.data as
                | IssuedLicenceRevokeSingleOut
                | undefined;
              await issuedLicencesQuery.refetch();
              maybeInvalidateUser();
              if (body && body.success === false) {
                const detail =
                  typeof body.detail === 'string' && body.detail.trim() !== ''
                    ? body.detail
                    : t('iam.users.licences.revoke_failed_fallback');
                open?.({
                  type: 'error',
                  message: t('notifications.error'),
                  description: `${detail} (HTTP ${body.upstream_status})`,
                });
                return;
              }
              open?.({
                type: 'success',
                message: t('notifications.success'),
                description: t('iam.users.licences.revoke_success'),
              });
            },
          }
        );
      },
    });
  };

  const handleRevokeAll = () => {
    if (!canRevoke?.can) {
      return;
    }
    const isGlobal = accountId == null && allowAllWhenNoAccount;
    const revokeAllUrl =
      accountId != null
        ? `account/issued-licences/accounts/${accountId}/revoke-all`
        : 'account/issued-licences/revoke-all';
    if (!isGlobal && accountId == null) {
      return;
    }
    Modal.confirm({
      title: t('iam.users.licences.revoke_all_confirm_title'),
      content: t('iam.users.licences.revoke_all_confirm_description'),
      onOk: () => {
        mutateRevoke(
          {
            url: revokeAllUrl,
            method: 'post',
            values: isGlobal ? { confirm: true } : {},
          },
          {
            onSuccess: async (result) => {
              const body = result?.data as
                | IssuedLicenceRevokeAllOut
                | undefined;
              await issuedLicencesQuery.refetch();
              maybeInvalidateUser();
              const failed = body?.failed ?? [];
              if (failed.length > 0) {
                const lines = failed
                  .map(
                    (row) => `${row.lcp_license_id}: HTTP ${row.status_code}`
                  )
                  .join('; ');
                const anyOk = (body?.success_ids?.length ?? 0) > 0;
                open?.({
                  type: 'error',
                  message: anyOk
                    ? t('iam.users.licences.revoke_all_partial')
                    : t('iam.users.licences.revoke_all_failed'),
                  description: lines,
                });
                return;
              }
              open?.({
                type: 'success',
                message: t('notifications.success'),
                description: t('iam.users.licences.revoke_all_success'),
              });
            },
          }
        );
      },
    });
  };

  const revokeDisabled =
    !canRevoke?.can ||
    revokeMutation.isPending ||
    (accountId == null && !allowAllWhenNoAccount) ||
    (issuedLicencesQuery.data?.data.length ?? 0) === 0;

  return (
    <>
      <Space
        align="center"
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
        wrap
      >
        <Title level={4} style={{ marginBottom: 0 }}>
          {t('iam.users.licences.section_title')}
        </Title>
        <Space wrap>
          {sortedIds.length > 1 && (
            <Select
              style={{ minWidth: 220 }}
              value={accountId ?? undefined}
              onChange={(value) => setSelectedAccountId(value)}
              options={sortedIds.map((id) => ({
                value: id,
                label: `Account #${id}`,
              }))}
            />
          )}
          <Button danger onClick={handleRevokeAll} disabled={revokeDisabled}>
            {t('iam.users.licences.revoke_all')}
          </Button>
        </Space>
      </Space>

      {accountId == null && !allowAllWhenNoAccount ? (
        <Alert
          type="info"
          showIcon
          message={t('account.licences.no_account_title')}
          description={t('account.licences.no_account_description')}
        />
      ) : (
        <Table<IssuedLicenceRow>
          dataSource={issuedLicencesQuery.data?.data ?? []}
          rowKey="id"
          loading={issuedLicencesQuery.isLoading}
          pagination={false}
          size="small"
        >
          <Table.Column
            dataIndex="account_email"
            title={t('account.licences.fields.account_email')}
            render={(value: string | null) => value ?? '-'}
          />
          <Table.Column
            dataIndex="publication_id"
            title={t('iam.users.licences.fields.publication_id')}
          />
          <Table.Column
            dataIndex="product_kind"
            title={t('iam.users.licences.fields.product_kind')}
          />
          <Table.Column
            dataIndex="lcp_license_id"
            title={t('iam.users.licences.fields.license_id')}
          />
          <Table.Column
            dataIndex="registered_device_count_max_observed"
            title={t('iam.users.licences.fields.devices')}
            render={(value: number | null) => value ?? '-'}
          />
          <Table.Column
            title={t('iam.users.licences.fields.overshared')}
            render={(_: unknown, row: IssuedLicenceRow) => {
              const isOvershared =
                row.overshare_flagged_at != null ||
                (row.registered_device_count_max_observed ?? 0) >=
                  OVERSHARE_DEVICE_THRESHOLD;
              if (!isOvershared) {
                return <Tag>{t('common.no')}</Tag>;
              }
              return <Tag color="red">{t('common.yes')}</Tag>;
            }}
          />
          <Table.Column
            title={t('iam.users.licences.fields.actions')}
            render={(_: unknown, row: IssuedLicenceRow) => (
              <Button
                danger
                size="small"
                onClick={() => handleRevokeSingle(row)}
                loading={revokeMutation.isPending}
                disabled={!canRevoke?.can}
              >
                {t('iam.users.licences.revoke')}
              </Button>
            )}
          />
        </Table>
      )}
    </>
  );
}
